import { useEffect, useState } from "react";
import api from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Twitch, Plug, PlugZap, Loader2, ExternalLink, CheckCircle2, XCircle, Gift, Square, PartyPopper, Volume2, X, Plus } from "lucide-react";
import { toast } from "sonner";
import { Switch } from "@/components/ui/switch";

const STATUS_UI = {
  connected: { color: "#22c55e", label: "Connected", Icon: CheckCircle2 },
  connecting: { color: "#eab308", label: "Connecting…", Icon: Loader2 },
  error: { color: "#ef4444", label: "Error", Icon: XCircle },
  disconnected: { color: "#71717a", label: "Not connected", Icon: XCircle },
};

export default function TwitchConnect() {
  const [status, setStatus] = useState({ status: "disconnected", connected: false });
  const [live, setLive] = useState(false);
  const [bot, setBot] = useState("");
  const [token, setToken] = useState("");
  const [channel, setChannel] = useState("");
  const [busy, setBusy] = useState(false);
  const [session, setSession] = useState(null);
  const [msgs, setMsgs] = useState(null);
  const [chat, setChat] = useState(null);
  const [ignoreInput, setIgnoreInput] = useState("");

  const load = async () => {
    try {
      const r = await api.get("/twitch/status");
      setStatus(r.data);
      setBot((b) => b || r.data.bot_username || "");
      setChannel((c) => c || r.data.channel || "");
    } catch (e) {}
    try { setSession((await api.get("/session")).data); } catch (e) {}
    try { const mr = await api.get("/config/messages"); setMsgs((m) => m || mr.data); } catch (e) {}
    try { const cr = await api.get("/config/chat"); setChat(cr.data); } catch (e) {}
  };
  useEffect(() => { load(); const t = setInterval(load, 3000); return () => clearInterval(t); }, []);
  useEffect(() => { const f = () => api.get("/twitch/live").then((r) => setLive(!!r.data.live)).catch(() => {}); f(); const t = setInterval(f, 30000); return () => clearInterval(t); }, []);

  const saveChat = async (patch) => {
    const r = await api.post("/config/chat", { ...chat, ...patch });
    setChat(r.data);
    return r.data;
  };
  const addIgnore = async () => {
    const name = ignoreInput.trim().replace(/^@/, "");
    if (!name) return;
    await saveChat({ ignore: [...new Set([...(chat.ignore || []), name])] });
    setIgnoreInput("");
    toast.success(`Ignoring ${name}`);
  };
  const removeIgnore = async (n) => { await saveChat({ ignore: (chat.ignore || []).filter((x) => x !== n) }); };

  const saveMsgs = async () => {
    const r = await api.post("/config/messages", msgs);
    setMsgs(r.data);
    toast.success("Message templates saved");
  };

  const testRaid = async () => {
    try {
      const r = await api.post("/raid/test", {});
      toast.success(`🎡 Wheel landed on ${r.data.multiplier}x for ${r.data.raider}! (open the overlay to see it)`);
      load();
    } catch (e) { toast.error("Test raid failed"); }
  };
  const endStream = async () => {
    await api.post("/session/end");
    toast.success("Stream ended — raid rewards cleared");
    load();
  };

  const connect = async () => {
    if (!bot.trim() || !token.trim() || !channel.trim()) { toast.error("Fill in all three fields"); return; }
    setBusy(true);
    try {
      const r = await api.post("/twitch/connect", { bot_username: bot, oauth_token: token, channel });
      setStatus(r.data);
      if (r.data.connected) toast.success(`Connected to #${r.data.channel}!`);
      else if (r.data.status === "error") toast.error(r.data.error || "Connection failed");
      else toast.message("Connecting…");
      setToken("");
    } catch (e) { toast.error(e.response?.data?.detail || "Connection failed"); }
    finally { setBusy(false); }
  };

  const disconnect = async () => {
    setBusy(true);
    try { setStatus((await api.post("/twitch/disconnect")).data); toast.success("Disconnected"); }
    finally { setBusy(false); }
  };

  const reconnect = async () => {
    setBusy(true);
    try {
      const r = await api.post("/twitch/reconnect");
      setStatus(r.data);
      if (r.data.connected) toast.success(`Reconnected to #${r.data.channel}!`);
      else if (r.data.status === "error") toast.error(r.data.error || "Reconnect failed");
      else toast.message("Reconnecting…");
    } catch (e) { toast.error(e.response?.data?.detail || "Reconnect failed"); }
    finally { setBusy(false); }
  };

  const s = STATUS_UI[status.status] || STATUS_UI.disconnected;

  return (
    <div className="space-y-6 max-w-3xl">
      <header>
        <h1 className="text-3xl md:text-4xl font-800 font-heading">Connect to Twitch</h1>
        <p className="text-muted-foreground mt-1 text-sm">Link your bot account so PancakeCupCake_ reads & replies in your real Twitch chat.</p>
      </header>

      {/* Status */}
      <div className="rounded-lg p-4 flex items-center gap-3 border" style={{ borderColor: `${s.color}66`, background: `${s.color}14` }} data-testid="twitch-status">
        <s.Icon className={`w-6 h-6 ${status.status === "connecting" ? "animate-spin" : ""}`} style={{ color: s.color }} />
        <div className="flex-1">
          <div className="font-heading font-700" style={{ color: s.color }}>{s.label}</div>
          {status.connected && <div className="text-xs text-muted-foreground">Bot <span className="text-white">{status.bot_username}</span> in <span className="text-white">#{status.channel}</span></div>}
          {status.status === "error" && status.error && <div className="text-xs text-red-300 mt-0.5">{status.error}</div>}
          <div className="text-[11px] mt-0.5 flex items-center gap-1.5" data-testid="twitch-live-status">
            <span className={`w-2 h-2 rounded-full ${live ? "bg-red-500 animate-pulse" : "bg-gray-500"}`} />
            <span className="text-muted-foreground">{live ? "🔴 Stream is LIVE — auto-payout & sneak game are active" : "Stream offline — going live is auto-detected from your saved token (no setup needed)"}</span>
          </div>
        </div>
        {status.connected && <span className="w-2.5 h-2.5 rounded-full bg-green-500 animate-pulse" />}
      </div>

      {/* Form */}
      <div className="bg-[#0a0a0a] border border-white/10 rounded-lg p-6 space-y-4">
        <div className="flex items-center gap-2 mb-1"><Twitch className="w-4 h-4 text-[#9146FF]" /><h3 className="font-heading font-700 text-sm">Bot credentials</h3></div>
        <div>
          <Label className="text-xs">Bot account username</Label>
          <Input value={bot} onChange={(e) => setBot(e.target.value)} placeholder="e.g. pancakecupcake_bot" className="bg-secondary border-white/10 mt-1" data-testid="twitch-bot-input" />
          <p className="text-[11px] text-muted-foreground mt-1">The Twitch account the bot logs in as (make a separate account for it, or use your own).</p>
        </div>
        <div>
          <Label className="text-xs">OAuth chat token</Label>
          <Input type="password" value={token} onChange={(e) => setToken(e.target.value)} placeholder={status.has_token ? "•••••• saved — leave blank to reuse" : "oauth:xxxxxxxxxxxxxxxxxxxxxxxxxxxxxx"} className="bg-secondary border-white/10 mt-1 font-mono" data-testid="twitch-token-input" />
          <p className="text-[11px] text-muted-foreground mt-1">
            Token for the BOT account (scopes chat:read + chat:edit). Get one at{" "}
            <a href="https://twitchtokengenerator.com" target="_blank" rel="noreferrer" className="text-primary hover:underline inline-flex items-center gap-0.5">twitchtokengenerator.com <ExternalLink className="w-3 h-3" /></a>.
            {status.has_token && <span className="text-cookie"> Your token is saved — just hit Reconnect, no need to regenerate it.</span>}
          </p>
        </div>
        <div>
          <Label className="text-xs">Channel to join</Label>
          <Input value={channel} onChange={(e) => setChannel(e.target.value)} placeholder="your_twitch_username" className="bg-secondary border-white/10 mt-1" data-testid="twitch-channel-input" />
          <p className="text-[11px] text-muted-foreground mt-1">Your main Twitch channel where the bot should chat/moderate. Make the bot account a <span className="text-white">moderator</span> there for timeouts.</p>
        </div>
        <div className="flex gap-2 pt-1">
          {status.connected ? (
            <Button onClick={disconnect} disabled={busy} variant="outline" className="border-white/10 rounded-lg" data-testid="twitch-disconnect-button"><PlugZap className="w-4 h-4 mr-2" />Disconnect</Button>
          ) : (
            <>
              <Button onClick={connect} disabled={busy} className="bg-[#9146FF] hover:bg-[#9146FF]/90 rounded-lg" data-testid="twitch-connect-button">
                {busy ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Plug className="w-4 h-4 mr-2" />}Connect
              </Button>
              {status.has_token && (
                <Button onClick={reconnect} disabled={busy} variant="outline" className="border-white/10 rounded-lg" data-testid="twitch-reconnect-button">
                  <PlugZap className="w-4 h-4 mr-2" />Reconnect (saved token)
                </Button>
              )}
            </>
          )}
        </div>
      </div>

      {/* Raid Reward / Session */}
      {session && (
        <div className="bg-[#0a0a0a] border border-white/10 rounded-lg p-5" data-testid="raid-reward-panel">
          <div className="flex items-center gap-2 mb-3"><Gift className="w-4 h-4 text-cookie" /><h3 className="font-heading font-700 text-sm">Raid Reward</h3></div>
          <p className="text-xs text-muted-foreground mb-4">On every raid, a wheel spins for a cookie multiplier. Everyone who joins from the raid earns that multiplier until you end the stream.</p>
          <div className="flex flex-wrap items-center gap-4">
            <div className={`px-4 py-3 rounded-lg border ${session.active ? "border-cookie/40 bg-cookie/10" : "border-white/10 bg-secondary"}`} data-testid="raid-buff-status">
              {session.active ? (
                <div className="flex items-center gap-2"><PartyPopper className="w-5 h-5 text-cookie" /><span className="font-heading font-800 text-cookie text-lg">{session.raid_multiplier}x active</span><span className="text-xs text-muted-foreground">· {session.member_count} raiders</span></div>
              ) : (
                <span className="text-sm text-muted-foreground">No active raid reward</span>
              )}
            </div>
            <div className="flex gap-2 ml-auto">
              <Button onClick={testRaid} variant="outline" className="border-white/10 rounded-lg" data-testid="test-raid-button"><Gift className="w-4 h-4 mr-2" />Test Raid Wheel</Button>
              <Button onClick={endStream} disabled={!session.active} className="bg-red-500 hover:bg-red-500/90 rounded-lg" data-testid="end-stream-button"><Square className="w-4 h-4 mr-2" />End Stream</Button>
            </div>
          </div>
          <p className="text-[11px] text-muted-foreground mt-3">Wheel odds: {session.wheel_segments?.join("x, ")}x · Open the <a href="/overlay" target="_blank" rel="noreferrer" className="text-primary hover:underline">overlay</a> to watch the wheel spin. Multiplier now lasts 1 hour.</p>
          {msgs && (
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mt-4">
              <div className="flex items-center justify-between rounded-lg bg-secondary px-4 py-3">
                <div><span className="text-sm">Speak raid alert (TTS)</span><p className="text-[11px] text-muted-foreground">She says the raid thank-you out loud</p></div>
                <Switch checked={msgs.raid_tts !== false} onCheckedChange={(v) => { setMsgs((m) => ({ ...m, raid_tts: v })); api.post("/config/messages", { raid_tts: v }); }} data-testid="raid-tts-toggle" />
              </div>
              <div className="flex items-center justify-between rounded-lg bg-secondary px-4 py-3">
                <div><span className="text-sm">Speak raid shoutout (TTS)</span><p className="text-[11px] text-muted-foreground">She reads the shoutout out loud</p></div>
                <Switch checked={msgs.shoutout_tts !== false} onCheckedChange={(v) => { setMsgs((m) => ({ ...m, shoutout_tts: v })); api.post("/config/messages", { shoutout_tts: v }); }} data-testid="shoutout-tts-toggle" />
              </div>
            </div>
          )}
        </div>
      )}

      {/* Chat voice & redeems */}
      {chat && (
        <div className="bg-[#0a0a0a] border border-white/10 rounded-lg p-5" data-testid="chat-voice-panel">
          <div className="flex items-center gap-2 mb-1"><Volume2 className="w-4 h-4 text-primary" /><h3 className="font-heading font-700 text-sm">Chat Voice & Redeems</h3></div>
          <p className="text-xs text-muted-foreground mb-4">Have PancakeCupCake_ read chat aloud, skip bots, and let viewers talk to her with a channel-point redeem.</p>

          <div className="flex items-center justify-between rounded-lg bg-secondary px-4 py-3 mb-4">
            <div><span className="text-sm">Read chat aloud (TTS)</span><p className="text-[11px] text-muted-foreground">She speaks every chat message (except commands & ignored users)</p></div>
            <Switch checked={chat.read_all} onCheckedChange={(v) => saveChat({ read_all: v })} data-testid="read-chat-toggle" />
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mb-4">
            <div className="flex items-center justify-between rounded-lg bg-secondary px-3 py-2 sm:col-span-1">
              <span className="text-xs">Skip links</span>
              <Switch checked={chat.skip_links} onCheckedChange={(v) => saveChat({ skip_links: v })} data-testid="skip-links-toggle" />
            </div>
            <div>
              <Label className="text-[11px] text-muted-foreground">Max message length to read</Label>
              <Input type="number" value={chat.max_read_len} onChange={(e) => setChat({ ...chat, max_read_len: parseInt(e.target.value) || 0 })} onBlur={() => saveChat({})} className="bg-secondary border-white/10 mt-1 h-9" data-testid="max-read-len-input" />
            </div>
            <div>
              <Label className="text-[11px] text-muted-foreground">Talk cooldown (seconds)</Label>
              <Input type="number" value={chat.talk_cooldown} onChange={(e) => setChat({ ...chat, talk_cooldown: parseInt(e.target.value) || 0 })} onBlur={() => saveChat({})} className="bg-secondary border-white/10 mt-1 h-9" data-testid="talk-cooldown-input" />
            </div>
          </div>

          <div className="mb-4">
            <Label className="text-xs">Don't read these users (bots)</Label>
            <div className="flex gap-2 mt-1">
              <Input value={ignoreInput} onChange={(e) => setIgnoreInput(e.target.value)} onKeyDown={(e) => e.key === "Enter" && addIgnore()} placeholder="e.g. Nightbot" className="bg-secondary border-white/10" data-testid="ignore-input" />
              <Button onClick={addIgnore} className="bg-primary hover:bg-primary/90" data-testid="add-ignore-button"><Plus className="w-4 h-4" /></Button>
            </div>
            <div className="flex flex-wrap gap-2 mt-2" data-testid="ignore-list">
              {(chat.ignore || []).map((n) => (
                <span key={n} className="flex items-center gap-1 text-xs px-2.5 py-1 rounded-full bg-secondary border border-white/10">{n}
                  <button onClick={() => removeIgnore(n)} className="text-muted-foreground hover:text-red-400" data-testid={`remove-ignore-${n}`}><X className="w-3 h-3" /></button>
                </span>
              ))}
              {(chat.ignore || []).length === 0 && <span className="text-xs text-muted-foreground">No ignored users yet.</span>}
            </div>
          </div>

          <div className="flex items-center justify-between rounded-lg bg-secondary px-4 py-3 mb-3">
            <div><span className="text-sm">Channel-point redeem → talk to Pancake</span><p className="text-[11px] text-muted-foreground">Make a reward that <span className="text-white">requires text</span>; redeemed messages go to her AI</p></div>
            <Switch checked={chat.redeem_enabled} onCheckedChange={(v) => saveChat({ redeem_enabled: v })} data-testid="redeem-toggle" />
          </div>
          <div>
            <Label className="text-xs">Reward ID <span className="text-muted-foreground">(leave blank = any text reward)</span></Label>
            <div className="flex gap-2 mt-1">
              <Input value={chat.redeem_reward_id || ""} onChange={(e) => setChat({ ...chat, redeem_reward_id: e.target.value })} onBlur={() => saveChat({})} placeholder="paste a reward ID, or leave blank" className="bg-secondary border-white/10 font-mono text-xs" data-testid="redeem-id-input" />
              {chat.last_redeem_id && <Button variant="outline" className="border-white/10 whitespace-nowrap" onClick={() => saveChat({ redeem_reward_id: chat.last_redeem_id })} data-testid="use-last-redeem">Use last</Button>}
            </div>
            {chat.last_redeem_id
              ? <p className="text-[11px] text-muted-foreground mt-1">Last detected redeem ID: <span className="text-cookie font-mono">{chat.last_redeem_id}</span></p>
              : <p className="text-[11px] text-muted-foreground mt-1">Redeem your reward once while connected and its ID will appear here.</p>}
          </div>
          <p className="text-[11px] text-muted-foreground mt-3">Viewers can also just type <span className="text-primary">!pancake your message</span> to talk to her anytime.</p>
        </div>
      )}

      {/* Editable message templates */}
      {msgs && (
        <div className="bg-[#0a0a0a] border border-white/10 rounded-lg p-5" data-testid="messages-editor">
          <h3 className="font-heading font-700 text-sm mb-1">Auto Shoutout & Raid Alert Text</h3>
          <p className="text-xs text-muted-foreground mb-4">Customize the wording. Placeholders get filled in automatically.</p>
          <div className="space-y-4">
            <div>
              <Label className="text-xs">Shoutout message <span className="text-muted-foreground">(leave blank to let PancakeCupCake_ write it with AI)</span></Label>
              <Textarea
                value={msgs.shoutout_template}
                onChange={(e) => setMsgs({ ...msgs, shoutout_template: e.target.value })}
                placeholder="📢 Go show @{user} some love! They were playing {game} — {url}"
                className="bg-secondary border-white/10 mt-1 font-mono text-sm"
                data-testid="shoutout-template-input"
              />
              <p className="text-[11px] text-muted-foreground mt-1">Placeholders: <span className="text-primary">{"{user}"}</span> <span className="text-primary">{"{title}"}</span> <span className="text-primary">{"{game}"}</span> <span className="text-primary">{"{url}"}</span></p>
            </div>
            <div>
              <Label className="text-xs">Raid alert message</Label>
              <Textarea
                value={msgs.raid_alert_template}
                onChange={(e) => setMsgs({ ...msgs, raid_alert_template: e.target.value })}
                placeholder="🎉 RAID!! Thank you @{user} for raiding with {count} viewers!"
                className="bg-secondary border-white/10 mt-1 font-mono text-sm"
                data-testid="raid-template-input"
              />
              <p className="text-[11px] text-muted-foreground mt-1">Placeholders: <span className="text-primary">{"{user}"}</span> <span className="text-primary">{"{count}"}</span> <span className="text-primary">{"{multiplier}"}</span></p>
            </div>
          </div>
          <div className="flex justify-end mt-4">
            <Button onClick={saveMsgs} className="bg-primary hover:bg-primary/90 rounded-lg" data-testid="save-messages-button">Save Text</Button>
          </div>
        </div>
      )}

      <div className="bg-[#0a0a0a] border border-white/10 rounded-lg p-5">
        <h3 className="font-heading font-700 text-sm mb-3">What happens when connected</h3>
        <ul className="text-sm text-muted-foreground space-y-2 list-disc pl-5">
          <li>Real viewer messages flow into the <span className="text-primary">Live Chat</span> logs, economy, and games.</li>
          <li>All your commands (<span className="text-primary">!fish</span>, <span className="text-primary">!gamble</span>, <span className="text-primary">!talk</span>, <span className="text-primary">!so</span>, custom ones) work in real chat.</li>
          <li>PancakeCupCake_'s replies are posted back to your channel and spoken on the overlay.</li>
          <li>Banned words trigger a timeout (the bot must be a mod in your channel).</li>
          <li>When someone <span className="text-white">raids</span>, she auto-shouts them out with their stream info.</li>
          <li>You & your mods can type <span className="text-primary">!mood happy/sad/mad/neutral/reset</span> (or a number) to fix her mood if it gets out of hand.</li>
        </ul>
      </div>
    </div>
  );
}
